<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Visao Geral dos Funis',
  },
});

function formatCurrency(value) {
  if (!value) return 'R$ 0';
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}

function getConversionColor(rate) {
  if (rate >= 30) return 'text-n-teal-11';
  if (rate >= 15) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-git-branch text-lg text-n-violet-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div
      v-else-if="data.length === 0"
      class="flex items-center justify-center h-48 text-n-slate-11"
    >
      <span class="text-sm">Nenhum funil encontrado</span>
    </div>

    <div v-else class="flex flex-col gap-4">
      <div
        v-for="funnel in data"
        :key="funnel.id"
        class="p-4 rounded-lg bg-n-alpha-1 border border-n-weak"
      >
        <!-- Header -->
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-2">
            <h4 class="font-semibold text-n-slate-12">{{ funnel.name }}</h4>
            <span
              v-if="funnel.is_default"
              class="text-xs px-2 py-0.5 rounded-full bg-n-blue-3 text-n-blue-11"
            >
              Padrao
            </span>
          </div>
          <span class="text-lg font-bold text-n-teal-11">{{
            formatCurrency(funnel.total_value)
          }}</span>
        </div>

        <!-- Stats Row -->
        <div class="grid grid-cols-4 gap-3 mb-3">
          <div class="text-center">
            <div class="text-lg font-bold text-n-slate-12">
              {{ formatNumber(funnel.total_deals) }}
            </div>
            <div class="text-xs text-n-slate-10">Total</div>
          </div>
          <div class="text-center">
            <div class="text-lg font-bold text-n-blue-11">
              {{ formatNumber(funnel.deals_open) }}
            </div>
            <div class="text-xs text-n-slate-10">Abertos</div>
          </div>
          <div class="text-center">
            <div class="text-lg font-bold text-n-teal-11">
              {{ formatNumber(funnel.deals_won) }}
            </div>
            <div class="text-xs text-n-slate-10">Ganhos</div>
          </div>
          <div class="text-center">
            <div class="text-lg font-bold text-n-ruby-11">
              {{ formatNumber(funnel.deals_lost) }}
            </div>
            <div class="text-xs text-n-slate-10">Perdidos</div>
          </div>
        </div>

        <!-- Conversion Rate -->
        <div
          class="flex items-center justify-between mb-3 p-2 rounded bg-n-alpha-2"
        >
          <span class="text-sm text-n-slate-11">Taxa de Conversao</span>
          <span
            class="font-bold"
            :class="getConversionColor(funnel.conversion_rate)"
          >
            {{ funnel.conversion_rate?.toFixed(1) || 0 }}%
          </span>
        </div>

        <!-- Stages -->
        <div
          v-if="funnel.stages_summary?.length > 0"
          class="flex flex-col gap-2"
        >
          <div class="text-xs font-medium text-n-slate-11 mb-1">Etapas</div>
          <div class="flex flex-wrap gap-1">
            <div
              v-for="stage in funnel.stages_summary"
              :key="stage.id"
              class="flex items-center gap-1 px-2 py-1 rounded text-xs"
              :style="{
                backgroundColor: `${stage.color}15`,
                borderLeft: `3px solid ${stage.color}`,
              }"
            >
              <span class="font-medium" :style="{ color: stage.color }">{{
                stage.name
              }}</span>
              <span class="text-n-slate-11">({{ stage.count }})</span>
              <span class="text-n-teal-11">{{
                formatCurrency(stage.value)
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
